import { Checkbox } from "@/components/ui/checkbox"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import type { LicenseData } from "@/hooks/useLicenseStore"
import { cn } from "@/lib/utils"
import type { FieldPath, UseFormReturn } from "react-hook-form"
import { Field } from "./Field"
import { ChoiceCard, type ChoiceOption } from "./ChoiceCard"
import { twMerge } from "tailwind-merge"

type Behaviour = "checkbox" | "radio"

type ChoiceFieldProps<TName extends FieldPath<LicenseData>> = {
  form: UseFormReturn<LicenseData>
  name: TName
  label?: string
  description?: string
  behaviour: Behaviour
  options: ChoiceOption[]
  className?: string
  optionClassName?: string
}

const GRID_LAYOUT = "grid grid-cols-1 gap-3 md:grid-cols-2"
const optionId = (name: FieldPath<LicenseData>, value: string) => `${String(name)}-${value}`

export const ChoiceField = <TName extends FieldPath<LicenseData>>({
  form,
  name,
  label,
  description,
  behaviour,
  options,
  className,
  optionClassName
}: ChoiceFieldProps<TName>) => {
  const gridClassName = cn(GRID_LAYOUT, className)

  const renderCheckbox = (field:any) => {
    const valueMap: Record<string, boolean> = field.value ?? {}
    const set = (key: string, next: boolean) => field.onChange({ ...valueMap, [key]: next })
    return (
      <div className={gridClassName}>
        {options.map(option => {
          const { value } = option
          const id = optionId(name, value)
          const checked = !!valueMap[value]
          return (
            <div key={value} className="relative">
              <input
                id={id} type="checkbox" className="sr-only" checked={checked}
                onChange={event => set(value, event.target.checked)}
              />
              <ChoiceCard id={id} option={option} isActive={checked} className={optionClassName}>
                <Checkbox
                  aria-hidden="true"
                  tabIndex={-1}
                  checked={checked}
                  className="pointer-events-none mt-1"
                />
              </ChoiceCard>
            </div>
          )
        })}
      </div>
    )
  }

  const renderRadio = (field:any) => (
  <RadioGroup className={twMerge(gridClassName, 'focus-within:ring-offset-0 pt-0.5')} value={field.value ?? ""} onValueChange={field.onChange}>
      {options.map(option => {
        const { value } = option
        const id = optionId(name, value)
        const selected = field.value === value
        return (
          <ChoiceCard key={value} id={id} option={option} isActive={selected} className={optionClassName}>
            <RadioGroupItem id={id} value={value} className="mt-1" />
          </ChoiceCard>
        )
      })}
    </RadioGroup>    
  )

  return <Field form={form} name={name} label={label} description={description} render={field => {
    if (behaviour === "checkbox") 
      return renderCheckbox(field)
    else
      return renderRadio(field)
    }} />
}
